import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PatrolTable from '../../components/PatrolTable';
import PatrolReplay from '../../components/PatrolReplay';
import { patrolApi, portalApi } from '../../services/portalApi';
import type { PatrolSession, PatrolPoint, AlarmEvent, IncidentEvent, Guard, Paginated } from '../../types';

export default function TrusteePatrols() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [result, setResult] = useState<Paginated<PatrolSession> | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [replayPatrol, setReplayPatrol] = useState<PatrolSession | null>(null);
  const [replayPoints, setReplayPoints] = useState<PatrolPoint[]>([]);
  const [replayAlarms, setReplayAlarms] = useState<AlarmEvent[]>([]);
  const [replayIncidents, setReplayIncidents] = useState<IncidentEvent[]>([]);
  const [replayLoading, setReplayLoading] = useState(false);
  const [guards, setGuards] = useState<Guard[]>([]);
  const [guardFilter, setGuardFilter] = useState(searchParams.get('guard_id') ?? '');
  const [typeFilter, setTypeFilter] = useState('');

  const replayId = searchParams.get('replay');

  useEffect(() => {
    setLoading(true);
    patrolApi.list(page, guardFilter || undefined).then(setResult).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, guardFilter]);

  useEffect(() => {
    portalApi.listGuards().then(setGuards).catch(() => {});
  }, []);

  // Reactive: open/close replay whenever ?replay= param changes
  useEffect(() => {
    if (!replayId) {
      setReplayPatrol(null);
      return;
    }
    setReplayLoading(true);
    patrolApi.get(replayId)
      .then(async (patrol) => {
        setReplayPatrol(patrol);
        const [pts, alarms, incidents] = await Promise.all([
          patrolApi.track(patrol.id),
          patrolApi.alarms(patrol.id),
          patrolApi.incidents(patrol.id),
        ]);
        setReplayPoints(pts);
        setReplayAlarms(alarms);
        setReplayIncidents(incidents);
      })
      .catch(() => {})
      .finally(() => setReplayLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayId]);

  // Push replay ID into the URL so browser back works correctly
  function handleReplay(patrol: PatrolSession) {
    const params: Record<string, string> = { replay: patrol.id };
    if (guardFilter) params.guard_id = guardFilter;
    setSearchParams(params);
  }

  const uniqueTypes = Array.from(
    new Set((result?.data ?? []).map((p) => p.patrol_type).filter(Boolean)),
  ) as string[];

  const filtered = (result?.data ?? []).filter(
    (p) => (!typeFilter || p.patrol_type === typeFilter),
  );

  if (replayPatrol) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-4 px-6 py-4 bg-white border-b flex-shrink-0">
          <button
            onClick={() => navigate(-1)}
            className="text-brand-600 hover:underline text-sm font-medium"
          >
            ← Back to patrols
          </button>
          <span className="font-semibold text-gray-900">{replayPatrol.guard_name}</span>
        </div>
        <div className="flex-1 p-4">
          {replayLoading ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              Loading track…
            </div>
          ) : (
            <PatrolReplay
              points={replayPoints}
              colour={replayPatrol.colour}
              guardName={replayPatrol.guard_name}
              alarms={replayAlarms}
              incidents={replayIncidents}
              className="h-full w-full"
            />
          )}
        </div>
      </div>
    );
  }

  const totalPages = result ? Math.ceil(result.total / result.per_page) : 1;

  return (
    <div className="px-6 py-6 max-w-5xl">
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-gray-900">Patrol History</h1>
        {result && <p className="text-sm text-gray-500">{result.total} patrols</p>}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={guardFilter}
          onChange={(e) => {
            const v = e.target.value;
            setGuardFilter(v);
            setPage(1);
            setSearchParams(v ? { guard_id: v } : {});
          }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        >
          <option value="">All patrollers</option>
          {guards.map((g) => (
            <option key={g.id} value={g.id}>
              {g.display_name}
            </option>
          ))}
        </select>
        {uniqueTypes.length > 0 && (
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          >
            <option value="">All types</option>
            {uniqueTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}
        {(guardFilter || typeFilter) && (
          <button
            onClick={() => {
              setGuardFilter('');
              setTypeFilter('');
              setPage(1);
              setSearchParams({});
            }}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <PatrolTable
          patrols={filtered}
          loading={loading}
          onViewReplay={handleReplay}
        />
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
